import React, {useState} from 'react'
import {Box, Button, Typography, useTheme} from "@mui/material"
import {AutoAwesome, CheckCircle, Cancel} from "@mui/icons-material"
import {doPost} from "../../../util/do-fetch"

const LETTERS = ['A', 'B', 'C', 'D']

const QuestionVariantBubble = ({ message }) => {
	const theme = useTheme()

	let parsed = null
	let parseError = false
	try {
		parsed = typeof message.body === 'string' ? JSON.parse(message.body) : message.body
	} catch (e) {
		parseError = true
	}

	// If previously answered (persisted), use that
	const [userAnswer, setUserAnswer] = useState(parsed?.userAnswer ?? null)

	if (parseError || !parsed) {
		return <Typography color="error">Could not parse variant question.</Typography>
	}
	const answered = userAnswer !== null
	const isCorrect = answered && userAnswer === parsed.answer

	const handleAnswer = async (index) => {
		if (answered) return
		setUserAnswer(index)

		// Persist the answer to the message
		try {
			await doPost(`/mini/message/${message.id}/variant-answer`, {
				body: JSON.stringify({ userAnswer: index })
			})
		} catch (err) {
			console.error('Error saving variant answer', err)
		}
	}

	return (
		<Box sx={{
			border: `1px solid ${theme.palette.divider}`,
			borderLeft: `3px solid ${theme.palette.info.main}`,
			borderRadius: '12px',
			p: 2,
			maxWidth: '100%',
			backgroundColor: theme.palette.mode === 'dark'
				? 'rgba(33, 150, 243, 0.08)'
				: 'rgba(33, 150, 243, 0.04)',
		}}>
			<Box display="flex" alignItems="center" gap={1} mb={1.5}>
				<AutoAwesome sx={{ fontSize: 16, color: theme.palette.info.main }} />
				<Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
					Try this variant
				</Typography>
			</Box>

			<Typography sx={{ fontSize: '15px', mb: 2, fontWeight: 500 }}>
				{parsed.question}
			</Typography>

			<Box display="flex" flexDirection="column" gap={1}>
				{parsed.options.map((opt, i) => {
					const isSelected = userAnswer === i
					const isCorrectAnswer = parsed.answer === i

					let borderColor = theme.palette.divider
					let bgColor = 'transparent'
					if (answered && isCorrectAnswer) {
						borderColor = '#4caf50'
						bgColor = 'rgba(46, 125, 50, 0.08)'
					} else if (answered && isSelected && !isCorrectAnswer) {
						borderColor = '#e53935'
						bgColor = 'rgba(229, 57, 53, 0.06)'
					}

					return (
						<Button
							key={i}
							variant="outlined"
							disabled={answered}
							onClick={() => handleAnswer(i)}
							sx={{
								textTransform: 'none',
								justifyContent: 'flex-start',
								textAlign: 'left',
								fontSize: '14px',
								py: 1,
								px: 2,
								borderColor: borderColor,
								backgroundColor: bgColor,
								color: theme.palette.text.primary,
								'&:hover': {
									borderColor: theme.palette.info.main,
									backgroundColor: 'rgba(33, 150, 243, 0.08)'
								},
								'&.Mui-disabled': {
									color: theme.palette.text.primary,
									borderColor: borderColor,
									backgroundColor: bgColor,
								}
							}}
						>
							<strong style={{ marginRight: 8 }}>{LETTERS[i]}.</strong> {opt.label}
						</Button>
					)
				})}
			</Box>

			{answered && (
				<Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', backgroundColor: isCorrect ? 'rgba(46, 125, 50, 0.06)' : 'rgba(229, 57, 53, 0.04)' }}>
					<Box display="flex" alignItems="center" gap={1} mb={0.5}>
						{isCorrect
							? <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />
							: <Cancel sx={{ fontSize: 18, color: '#e53935' }} />
						}
						<Typography variant="body2" sx={{ fontWeight: 600, color: isCorrect ? '#2e7d32' : '#c62828' }}>
							{isCorrect ? 'Correct!' : `Incorrect — the answer is ${LETTERS[parsed.answer]}. ${parsed.options[parsed.answer].label}`}
						</Typography>
					</Box>
					{parsed.explanation && (
						<Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
							{parsed.explanation}
						</Typography>
					)}
				</Box>
			)}
		</Box>
	)
}

export default QuestionVariantBubble
